import { AccountStat } from '@kwenta/sdk/types'
import { formatDollars, formatNumber, truncateAddress, truncateNumbers } from '@kwenta/sdk/utils'
import Wei from '@kwenta/wei'
import { FC, memo, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { normalize } from 'viem/ens'
import { useEnsAvatar } from 'wagmi'

import DefaultProfileIcon from 'assets/svg/app/default-profile.svg'
import CopyCheckIcon from 'assets/svg/referrals/copy-check.svg'
import { FlexDivCol, FlexDivRowCentered } from 'components/layout/flex'
import { Body } from 'components/Text'
import { NO_VALUE } from 'constants/placeholder'
import { selectKwentaPrice } from 'state/earn/selectors'
import { useAppSelector } from 'state/hooks'
import { selectKwentaRewards, selectStakedKwentaBalance } from 'state/staking/selectors'
import media from 'styles/media'

type Props = {
	amount: string
	value: string
}

const AmountContainer: FC<Props> = memo(({ amount, value }) => {
	return (
		<FlexDivRowCentered columnGap="5px" justifyContent="flex-start">
			<StyledBody color="preview" weight="semiBold">
				{amount}
			</StyledBody>
			<Body size="small">{value}</Body>
		</FlexDivRowCentered>
	)
})

const TraderProfileHeader: FC<AccountStat<Wei, number>> = memo(
	({ trader, traderShort, traderEns, rank, tier, accountId }) => {
		const { t } = useTranslation()
		const stakedKwentaBalance = useAppSelector(selectStakedKwentaBalance)
		const kwentaPrice = useAppSelector(selectKwentaPrice)
		const kwentaRewards = useAppSelector(selectKwentaRewards)
		const [copiedStatus, setCopiedStatus] = useState<boolean>(false)
		const [tooltipPosition, setTooltipPosition] = useState<{ x: number; y: number } | null>(null)
		const { data: ensAvatarIcon } = useEnsAvatar({
			name: normalize(traderEns ?? ''),
			chainId: 1,
		})

		const stakedKwentaBalanceUSD = useMemo(
			() => stakedKwentaBalance.mul(kwentaPrice),
			[kwentaPrice, stakedKwentaBalance]
		)

		const kwetnaRewardsUSD = useMemo(
			() => kwentaRewards.mul(kwentaPrice),
			[kwentaPrice, kwentaRewards]
		)

		const displayTraderAddress = useMemo(() => {
			if (traderShort) {
				return traderShort
			}
			if (trader) {
				return truncateAddress(trader)
			}
			return NO_VALUE
		}, [trader, traderShort])

		const handleCopyToClipboard = useCallback((text: string, event: React.MouseEvent) => {
			const x = event.clientX
			const y = event.clientY

			navigator.clipboard.writeText(text).then(() => {
				setCopiedStatus(true)
				setTooltipPosition({ x, y })

				setTimeout(() => {
					setCopiedStatus(false)
					setTooltipPosition(null)
				}, 1000)
			})
		}, [])

		const userProfile = useMemo(
			() => [
				{
					label: t('leaderboard.profile.uid'),
					component: (
						<FlexDivRowCentered columnGap="5px">
							<StyledBody>{accountId ? truncateAddress(accountId) : NO_VALUE}</StyledBody>
							{accountId && (
								<StyledCopyCheckIcon
									onClick={(event: React.MouseEvent) =>
										handleCopyToClipboard(accountId.toString(), event)
									}
								/>
							)}
						</FlexDivRowCentered>
					),
				},
				{
					label: t('leaderboard.profile.level'),
					value: tier ?? NO_VALUE,
				},
				{
					label: t('leaderboard.profile.leaderboard'),
					value: rank === -1 ? NO_VALUE : `#${rank}`,
				},
				{
					label: t('leaderboard.profile.kwenta-staked'),
					component: (
						<AmountContainer
							amount={formatNumber(stakedKwentaBalance, { suggestDecimals: true })}
							value={formatDollars(stakedKwentaBalanceUSD)}
						/>
					),
				},
				{
					label: t('leaderboard.profile.rewards'),
					component: (
						<AmountContainer
							amount={truncateNumbers(kwentaRewards, 4)}
							value={formatDollars(kwetnaRewardsUSD)}
						/>
					),
				},
			],
			[
				accountId,
				handleCopyToClipboard,
				kwentaRewards,
				kwetnaRewardsUSD,
				rank,
				stakedKwentaBalance,
				stakedKwentaBalanceUSD,
				t,
				tier,
			]
		)

		return (
			<ProfileContainer justifyContent="flex-start" columnGap="25px">
				<LabelContainer columnGap="15px">
					{ensAvatarIcon ? (
						<StyledProfileImage src={ensAvatarIcon} alt={(traderEns ?? '').toString()} />
					) : (
						<DefaultProfileIcon />
					)}
					<FlexDivCol rowGap="2px">
						{traderEns ? (
							<StyledBody>{traderEns}</StyledBody>
						) : (
							<StyledBody color="secondary">{t('leaderboard.profile.trader')}</StyledBody>
						)}
						<FlexDivRowCentered justifyContent="flex-start" columnGap="5px">
							<StyledBody color={traderEns ? 'secondary' : 'primary'}>
								{displayTraderAddress}
							</StyledBody>
							{displayTraderAddress !== NO_VALUE && (
								<StyledCopyCheckIcon
									onClick={(event: React.MouseEvent) => handleCopyToClipboard(trader, event)}
								/>
							)}
						</FlexDivRowCentered>
					</FlexDivCol>
				</LabelContainer>
				<MetricsContainer columnGap="25px" justifyContent="flex-start">
					{userProfile.map(({ label, value, component }) => (
						<FlexDivCol key={label} rowGap="2px">
							<StyledBody color="secondary" size="small">
								{label}
							</StyledBody>
							{value && <StyledBody>{value}</StyledBody>}
							{component && component}
						</FlexDivCol>
					))}
				</MetricsContainer>
				{copiedStatus && tooltipPosition && (
					<div>
						<StyledTooltip
							top={`${tooltipPosition.y + 10}px`}
							left={`${tooltipPosition.x + 10}px`}
							padding="5px"
							borderRadius="8px"
						>
							<Body>Code Copied!</Body>
						</StyledTooltip>
					</div>
				)}
			</ProfileContainer>
		)
	}
)

const StyledBody = styled(Body)`
	${media.lessThan('sm')`
		font-size: 9px;
	`}
`
const StyledProfileImage = styled.img`
	border-radius: 50%;
	border: 1px solid ${(props) => props.theme.colors.selectedTheme.newTheme.text.tertiary};
	width: 35px;
	height: 35px;
`

const StyledTooltip = styled.div<{
	top?: string
	left?: string
	padding?: string
	borderRadius?: string
}>`
	position: fixed;
	top: ${(props) => props.top};
	left: ${(props) => props.left};
	padding: ${(props) => props.padding};
	border-radius: ${(props) => props.borderRadius};
	background: ${(props) => props.theme.colors.selectedTheme.button.fill};
	border: ${(props) => props.theme.colors.selectedTheme.border};
	box-shadow: 0 0 10px rgba(0, 0, 0, 0.2);
	transition: opacity 0.3s;
	opacity: 1;
`

const StyledCopyCheckIcon = styled(CopyCheckIcon)`
	color: ${(props) => props.theme.colors.selectedTheme.newTheme.text.secondary};
	cursor: pointer;
`

const LabelContainer = styled(FlexDivRowCentered)`
	border-right: ${(props) => props.theme.colors.selectedTheme.border};
	padding-right: 25px;

	${media.lessThan('md')`
		column-gap: 10px;
		padding-right: 10px;
	`}
`

const MetricsContainer = styled(FlexDivRowCentered)`
	flex-wrap: wrap;
	row-gap: 15px;
`
const ProfileContainer = styled(FlexDivRowCentered)`
	${media.lessThan('md')`
		column-gap: 10px;
	`}
`
export default TraderProfileHeader
